import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  favorite,
  favoriteFilled,
  favoriteHovered,
} from 'static/icons/resizable';
import { t } from 'i18next';
import { useAppSelector, useAppDispatch } from 'redux/hooks';
import { authSelectors } from 'redux/modules/Authorization';
import { toggleFavoriteThunk } from 'redux/modules/Favorites/favorties.thunk';
import { showSuccessAlert, showErrorAlert } from 'redux/utils';
import { IStandardErrorResponseFromBE } from 'services';
import { AxiosError } from 'axios';
import { DiscountType, AlertDictionaryDiscountBookmark } from 'interfaces';
import { useCurrentLocation, useUserRoleId } from 'hooks';
import { captureEvent } from 'utils/analytics/captureEvent';
import { AnalyticsEvents } from 'enums';
import * as S from './FavoriteButton.styles';

type FavoriteButtonProps = {
  discountId?: number;
  isFavorite?: boolean;
  discountTitle?: string;
  itemCategory?: string;
};

export const FavoriteButton = ({
  discountId,
  isFavorite = false,
  discountTitle = 'Discount',
  itemCategory = '',
}: FavoriteButtonProps) => {
  const params = useParams();
  const dispatch = useAppDispatch();
  const user = useAppSelector(authSelectors.getUser);
  const currentDiscountId = discountId ?? Number(params.discountId);

  const [loading, setLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const roleId = useUserRoleId();
  const currentLocation = useCurrentLocation();

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user || !currentDiscountId || !currentLocation?.id) {
      showErrorAlert(
        dispatch,
        new Error(),
        t('alert.ERRORS.favorite_sync_error.title'),
      );
      return;
    }

    if (!isFavorite) {
      captureEvent(AnalyticsEvents.AddToFavoriteButtonClick, {
        user_id: String(user.id),
        role_id: roleId,
        item_id: String(currentDiscountId),
        item_name: discountTitle,
        item_category: itemCategory,
      });
    }

    setLoading(true);

    try {
      await dispatch(
        toggleFavoriteThunk({
          discountId: currentDiscountId,
          locationId: currentLocation.id,
        }),
      );
      const newState = !isFavorite;

      showSuccessAlert({
        dispatch,
        discountType: DiscountType.SHOW_AT_LOCATION,
        discountId: currentDiscountId,
        discountTitle,
        operation: newState
          ? AlertDictionaryDiscountBookmark.favorite_add
          : AlertDictionaryDiscountBookmark.favorite_remove,
      });
    } catch (error: unknown) {
      const axiosError = error as AxiosError<IStandardErrorResponseFromBE>;

      if (!navigator.onLine) {
        showErrorAlert(
          dispatch,
          error,
          t('alert.ERRORS.favorite_network_error.title'),
        );
      } else if (axiosError.response?.status === 408) {
        showErrorAlert(
          dispatch,
          error,
          t('alert.ERRORS.favorite_timeout_error.title'),
        );
      } else if (axiosError.response?.status === 500) {
        showErrorAlert(
          dispatch,
          error,
          t('alert.ERRORS.favorite_database_error.title'),
        );
      } else {
        showErrorAlert(
          dispatch,
          error,
          t('alert.ERRORS.favorite_sync_error.title'),
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const favoriteIcon = useMemo(() => {
    if (!isFavorite && isHovered) return favoriteHovered;
    if (isFavorite) return favoriteFilled;
    return favorite;
  }, [isFavorite, isHovered]);

  return (
    <S.FavoriteButtonStyle
      data-testid={`favorite-button-${discountId}`}
      type='button'
      kind='custom'
      onClick={handleFavoriteClick}
      disabled={loading}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <S.CustomFavoriteIcon
        data-testid={`favorite-icon-${discountId}`}
        src={favoriteIcon}
        alt='Favorite icon'
        style={{
          borderWidth: '1.4px',
          position: 'relative',
        }}
      />
    </S.FavoriteButtonStyle>
  );
};
